'use client';

import type { HTMLAttributes, ReactNode } from 'react';

type DSSurfaceProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
};

/**
 * Marks a subtree as design-system territory.
 *
 * `src/app/design-system-compat.css` holds a set of zero-specificity guards that
 * keep DS class names (`.alert`, `.card`, `.modal`, `.p-0`, …) from restyling
 * the legacy Bootstrap markup that already uses those same names app-wide. Each
 * guard is written `:where(.x:not([data-lula-ds], [data-lula-ds] *))`, so the marked
 * element and everything inside it are exempt and DS components render normally.
 *
 * Every region built out of DS components needs this attribute somewhere above
 * it. `SettingsRow` and `SettingsPageHeader` set it themselves, so a region
 * assembled from those is already covered; reach for `DSSurface` when placing
 * raw DS components outside of them.
 *
 * Caveat: DS `Modal` renders through a portal on document.body, outside any
 * DSSurface. Converting a modal means passing `data-lula-ds=""` as a prop to
 * `Modal` itself — it spreads unrecognised props onto its dialog panel div, the
 * one element everything the modal renders is a descendant of (see
 * `SettingsDiscardModal`).
 */
export const DSSurface = ({ children, ...props }: DSSurfaceProps) => (
  <div data-lula-ds="" {...props}>
    {children}
  </div>
);

export default DSSurface;
